import React, { useState, useContext, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import Script from 'next/script';
import { useRouter } from 'next/router';
import { modelNameTransformer } from 'pubweb-smokey/dist/utils/modelNameTransformer';
import { getLocalStorage } from 'pubweb-smokey/dist/utils/utils';
import { getSiteUrl } from '@utils/config';
import { LocationsContext } from '@contexts/LocationsContext';
import * as tokenService from '@services/tokenService';
import Heading from '@components/HomeDetails/Heading/Heading';
import { HomeDetailsStyles } from './HomeDetails.styled';
import {
  getModelByModelNumber,
  insertModelIntoRecentlyViewed,
} from '@services/homesService';
import FeaturedHomes from '@components/Home/FeaturedHomes/FeaturedHomes';
import { isHomeInStock } from '@components/HomeDetails/isHomeInStock';
import DynamicDisclaimer from '@components/Shared/Disclaimer/DynamicDisclaimer';
import PreFooterSection from '@components/Shared/PreFooterSection/PreFooterSection';
import FindARetailer from '@components/Shared/FindARetailer/FindARetailer';
import ImageGallery from '@components/HomeDetails/ImageGallery/ImageGallery';
import StructuredData from '@components/HomeDetails/StructuredData/StructuredData';
import StickyNav from '@components/HomeDetails/StickyNav/StickyNav';
import MoveInReady from '@components/HomeDetails/MoveInReady/MoveInReady';
import GoogleMap from '@components/HomeDetails/GoogleMap/GoogleMap';
import { isMobile } from 'pubweb-smokey/dist/utils/utils';
import {
  desktop_breakpoint,
  desktop_breakpoint_extended,
} from 'pubweb-smokey/dist/components/GridSystem/_vars_widths.js';
import LearnMore from './LearnMore/LearnMore';
import { formatStringToDisplayCase } from 'pubweb-smokey/dist/utils/formatters';
import EmailSignupContainer from '@components/Shared/EmailSignupContainer/EmailSignupContainer';
import useWindowResize from 'pubweb-smokey/dist/hooks/useWindowResize';
import LogoLake from './LogoLake/LogoLake';
import { getMarketingParagraph } from './getMarketingParagraph';

export const imageBtnOptions = {
  virtTour: '3dtour',
  floorplan: 'floorplan',
};
import { moveInReadySerialNumbers } from '@utils/moveInReadyHideImagesList';

const onLandDisclaimer = `&ast; Sales price does not include other costs such as taxes, title fees, insurance premiums, filing or recording fees, improvements to the land or home, community or homeowner association fees, or any other items not shown on your Sales Agreement, Retailer Closing Agreement and related documents (your SA/RCA). If you purchase a home, your SA/RCA will show the details of your purchase. Artists’ renderings of homes are only representations and actual home may vary. Floor plan dimensions are approximate and based on length and width measurements from exterior wall to exterior wall. We invest in continuous product and process improvement. All home series, floor plans, specifications, dimensions, features, materials, and availability shown on this website are subject to change.

&ast;&ast; [ZERH Logo Use Guidelines (energy.gov)](https://www.energy.gov/sites/default/files/2022-11/ZERH%20Name%20and%20Logo%20Use%20Guidelines_0.pdf)`;

const Modal = dynamic(() => import('@components/Shared/Modal/Modal'), {
  ssr: true,
});

const ScheduleVisit = dynamic(
  () => import('@components/HomeDetails/ScheduleVisit/ScheduleVisit'),
  { ssr: false }
);

const TopFeatures = dynamic(
  () => import('@components/HomeDetails/TopFeatures/TopFeatures'),
  { ssr: false }
);

export const HomeDetails = ({ homeDetails, pageInfo, dealerInfo }) => {
  const [price, setPrice] = useState();
  const [isPriceLoading, setIsPriceLoading] = useState(true);
  const [isInStock, setIsInStock] = useState(false);
  const [recentHomesUpdated, setRecentHomesUpdated] = useState(false);
  const [emailSignup, setEmailSignup] = useState(false);
  const [displaySticky, setDisplaySticky] = useState(false);
  const [isMobileLayout, setIsMobileLayout] = useState(true);
  const [formData, saveFormData] = useState({});
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [showScheduleVisitForm, setShowScheduleVisitForm] = useState(false);
  const visitorToken = tokenService.getVisitorToken();
  const locationsContext = useContext(LocationsContext);
  const router = useRouter();

  if (
    homeDetails.isOnLand &&
    moveInReadySerialNumbers.includes(homeDetails.serialNumber)
  ) {
    router.push('/find-a-home/');
  }

  const homeDetailsPage = useMemo(() => {
    return {
      minSquareFeet: 0,
      maxSquareFeet: homeDetails.maxSquareFeet || homeDetails.squareFeet,
      baths: homeDetails.baths,
      beds: homeDetails.beds,
      minPrice: homeDetails.isOnLand ? 0 : price,
      maxPrice: homeDetails.isOnLand ? homeDetails.priceSort : price,
      isMultiSection: null,
      isCrossMod: null,
      sortId: null,
      features: homeDetails.tags,
      modelNumber: homeDetails.modelNumber,
    };
  }, [homeDetails, price]); // memoized this object because <FeaturedHomes> component would re-render when scrolling on the detail page

  const [imageBtnSelection, setImageBtnSelection] = useState('');

  const locationAddress = (() => {
    const { address1, address2, city, stateProvince, postalCode } = homeDetails;
    const isStreetAddressValid =
      /^(\d{1,}) [a-zA-Z0-9\s]+(\\,)? [a-zA-Z]+(\\,)?$/.test(address1);

    return [
      address1 && isStreetAddressValid
        ? `${formatStringToDisplayCase(address1)},`
        : null,
      address2 && address2 && `${formatStringToDisplayCase(address2)},`,
      city && city !== 'Tbd' ? `${formatStringToDisplayCase(city)},` : null,
      stateProvince,
      postalCode,
    ].join(' ');
  })();

  const updateRecentlyViewed = () => {
    let geolocation = locationsContext.state.geocode;
    let { distance } = locationsContext.state;

    // Home details for homes with land items do not have a modelId. InsertModelIntoRecentlyViewed will generate an error if we call it.
    if (homeDetails?.modelId) {
      insertModelIntoRecentlyViewed(
        homeDetails.modelId,
        visitorToken,
        geolocation.latitude,
        geolocation.longitude,
        distance
      ).then((result) => {
        if (typeof visitorToken === 'undefined' || visitorToken === null) {
          tokenService.setVisitorToken(result.visitorToken);
          setRecentHomesUpdated(true);
        }
      });
    }
  };

  const getPriceAndStockInfo = () => {
    // Unfortunately, this is necessary right now because we need to provide a location
    // to getModelByModelNumber() for pricing and inStock status, but
    // the locationContext isn't available in getInitialProps when we make the first call
    if (locationsContext?.state?.geocode) {
      getModelByModelNumber(
        homeDetails.modelNumber,
        locationsContext.state.geocode.latitude,
        locationsContext.state.geocode.longitude,
        locationsContext.state.distance
      ).then((result) => {
        setIsPriceLoading(false);
        if (result?.dealers?.length > 0) {
          setIsInStock(isHomeInStock(result.dealers));
          if (!homeDetails?.isOnLand) {
            setPrice(result.startingInThePrice);
          } else {
            setPrice(homeDetails?.priceLabel);
          }
        } else {
          setIsInStock(false);
          setPrice(null);
        }
      });
    }
  };

  const scrollToContainer = (idName) => {
    const yOffset = isMobile(desktop_breakpoint) ? -54 : -85; // account for the sticky nav
    let ele = document.getElementById(idName);
    if (ele) {
      const y = ele.getBoundingClientRect().top + window.scrollY + yOffset;
      window.scrollTo({ top: y });
    }
  };

  const modelName = modelNameTransformer(
    homeDetails.modelName
      ? homeDetails.modelName?.replace(/\s+/g, ' ')
      : homeDetails.modelDescription?.replace(/\s+/g, ' ')
  );
  const modelUrl = getSiteUrl(router.asPath);
  let exteriorImage = null;
  let interiorImage = null;

  homeDetails.images.forEach((image) => {
    if (image.imageTypeAcronym === 'EXT' && !exteriorImage) {
      exteriorImage = image.reference;
    }

    if (image.imageTypeAcronym === 'INT' && !interiorImage) {
      interiorImage = image.reference;
    }
  });

  useWindowResize(() => {
    if (typeof window !== 'undefined') {
      if (window.innerWidth > desktop_breakpoint_extended) {
        setIsMobileLayout(false);
      } else {
        setIsMobileLayout(true);
      }
    }
  }, [desktop_breakpoint_extended]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (getLocalStorage('emailPopupClosed') !== true) {
        setEmailSignup(true);
      }
    }, 10000);
    return () => {
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    getPriceAndStockInfo();
    if (locationsContext.state.geocode !== null && !recentHomesUpdated) {
      updateRecentlyViewed();
    }
  }, [
    locationsContext.state.geocode,
    locationsContext.state.distance,
    router.asPath,
    homeDetails,
  ]);

  const checkSticky = () => {
    // eslint-disable-next-line prefer-destructuring
    const headingElement = document.getElementsByClassName(
      'home-details-heading'
    )[0];

    const rect = headingElement.getBoundingClientRect();

    const elementOnScreen = rect.top < window.innerHeight && rect.bottom >= 0;

    if (!elementOnScreen && !displaySticky) {
      setDisplaySticky(true);
    } else if (elementOnScreen && !displaySticky) {
      setDisplaySticky(false);
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', checkSticky);

    return () => {
      window.removeEventListener('scroll', checkSticky);
    };
  }, []);

  const marketingParagraph = getMarketingParagraph({
    modelName,
    features: homeDetails.tags || homeDetails.modelFeatures,
    baths: homeDetails.baths,
    beds: homeDetails.beds,
    squareFeet: homeDetails.squareFeet || homeDetails.maxSquareFeet,
  });

  return (
    <HomeDetailsStyles
      $hasVirtualTour={
        homeDetails.virtualTours?.[0]?.virtualTourReference ? true : false
      }
    >
      <Head htmlAttributes={{ lang: 'en' }}>
        <title>{`${modelName} | Clayton Homes`}</title>
        <meta
          name="description"
          content={`View ${modelName} floor plan, price, square feet and where to buy! Start your home search by requesting more information.`}
        />
        <link rel="canonical" href={modelUrl} />
        <meta property="og:image" content={homeDetails?.images[0]?.reference} />
      </Head>
      {/* Start VWO Async SmartCode */}
      <Script
        id="vwoCode"
        dangerouslySetInnerHTML={{
          __html: `
                window._vwo_code=window._vwo_code || (function() {
                var account_id=327243,
                version = 1.5,
                settings_tolerance=2000,
                library_tolerance=2500,
                use_existing_jquery=false,
                is_spa=1,
                hide_element='body',
                hide_element_style = 'opacity:0 !important;filter:alpha(opacity=0) !important;background:none !important',
                /* DO NOT EDIT BELOW THIS LINE */
                f=false,w=window,d=document,vwoCodeEl=d.querySelector('#vwoCode'),code={use_existing_jquery:function(){return use_existing_jquery},library_tolerance:function(){return library_tolerance},hide_element_style:function(){return'{'+hide_element_style+'}'},finish:function(){if(!f){f=true;var e=d.getElementById('_vis_opt_path_hides');if(e)e.parentNode.removeChild(e)}},finished:function(){return f},load:function(e){var t=d.createElement('script');t.fetchPriority='high';t.src=e;t.type='text/javascript';t.onerror=function(){_vwo_code.finish()};d.getElementsByTagName('head')[0].appendChild(t)},getVersion:function(){return version},getMatchedCookies:function(e){var t=[];if(document.cookie){t=document.cookie.match(e)||[]}return t},getCombinationCookie:function(){var e=code.getMatchedCookies(/(?:^|;)\\s?(_vis_opt_exp_\\d+_combi=[^;$]*)/gi);e=e.map(function(e){try{var t=decodeURIComponent(e);if(!/_vis_opt_exp_\\d+_combi=(?:\\d+,?)+\\s*$/.test(t)){return''}return t}catch(e){return''}});var i=[];e.forEach(function(e){var t=e.match(/([\\d,]+)/g);t&&i.push(t.join('-'))});return i.join('|')},init:function(){if(d.URL.indexOf('__vwo_disable__')>-1)return;w.settings_timer=setTimeout(function(){_vwo_code.finish()},settings_tolerance);var e=d.currentScript,t=d.createElement('style'),i=e&&!e.async?hide_element?hide_element+'{'+hide_element_style+'}':'':code.lA=1,n=d.getElementsByTagName('head')[0];t.setAttribute('id','_vis_opt_path_hides');vwoCodeEl&&t.setAttribute('nonce',vwoCodeEl.nonce);t.setAttribute('type','text/css');if(t.styleSheet)t.styleSheet.cssText=i;else t.appendChild(d.createTextNode(i));n.appendChild(t);var o=this.getCombinationCookie();this.load('https://dev.visualwebsiteoptimizer.com/j.php?a='+account_id+'&u='+encodeURIComponent(d.URL)+'&f='+ +is_spa+'&vn='+version+(o?'&c='+o:''));return settings_timer}};w._vwo_settings_timer = code.init();return code;}());
              `,
        }}
      />
      {/* End VWO Async SmartCode */}
      <StructuredData
        name={modelName}
        imageUrl={exteriorImage ? exteriorImage : interiorImage}
        url={modelUrl}
        price={price}
        availability={isInStock ? 'InStock' : 'OutOfStock'}
      />

      {emailSignup ? (
        <EmailSignupContainer
          enableShowDx={false}
          pageType="homedetail"
          onCloseEmailPopup={() => {
            setEmailSignup(false);
          }}
          modelNumber={homeDetails?.modelNumber}
        />
      ) : null}

      {!isMobileLayout && showScheduleVisitForm && (
        <div className="request-form">
          <Modal onClose={() => setShowScheduleVisitForm(false)}>
            <ScheduleVisit
              homeDetails={homeDetails}
              formOpen={showScheduleVisitForm}
              formData={formData}
              saveFormData={saveFormData}
              onSubmit={() => setFormSubmitted(true)}
              showThankYou={formSubmitted}
            />
          </Modal>
        </div>
      )}

      {displaySticky ? (
        <StickyNav
          homeDetails={homeDetails}
          onFeaturesClick={() => scrollToContainer('features-container')}
          onFindRetailerClick={() => scrollToContainer('find-retailer')}
          onLearnMoreClick={() => scrollToContainer('learn-more')}
          onScheduleVisitClick={() => {
            if (isMobile) {
              scrollToContainer('schedule-visit-click');
            }
            setShowScheduleVisitForm(true);
          }}
        />
      ) : null}

      <div className="constrained-width images-titles-section">
        <div className="image-gallery-grid">
          <ImageGallery
            home={homeDetails}
            imageBtnSelection={imageBtnSelection}
            clearImageBtnSelection={() => {
              setImageBtnSelection('');
            }}
          ></ImageGallery>
        </div>
        <div className="titles-section">
          <Heading
            homeDetails={homeDetails}
            isInStock={isInStock}
            price={price}
            onFindRetailerClick={() =>
              scrollToContainer(
                `${homeDetails.isOnLand ? 'learn-more' : 'find-retailer'}`
              )
            }
            onScheduleVisitClick={() => setShowScheduleVisitForm(true)}
            dealerInfo={homeDetails.isOnLand && dealerInfo}
            onImageButtonClick={(b) => setImageBtnSelection(b)}
            isMobile={isMobileLayout}
            formSubmitted={formSubmitted}
            setFormSubmitted={setFormSubmitted}
            showScheduleVisitForm={showScheduleVisitForm}
            setShowScheduleVisitForm={setShowScheduleVisitForm}
          />
          {homeDetails && (
            <>
              <p className="pb-12 !text-sm">{marketingParagraph}</p>
              <div id="features-container">
                <TopFeatures
                  features={homeDetails.tags || homeDetails.modelFeatures}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {homeDetails.isOnLand && (
        <div className="constrained-width map-section">
          <h2>Location</h2>
          <p>{locationAddress}</p>
          <div className="map-container">
            <GoogleMap
              city={homeDetails.city}
              stateProvince={homeDetails.stateProvince}
              address1={homeDetails.address1}
              postalCode={homeDetails.postalCode}
            />
          </div>
        </div>
      )}

      {homeDetails.isOnLand && (
        <div className="constrained-width">
          <MoveInReady />
        </div>
      )}

      {homeDetails.isOnLand && (
        <div id="learn-more" className="learn-more-container">
          <div className="constrained-width">
            <LearnMore dealerInfo={dealerInfo} />
          </div>
        </div>
      )}

      <LogoLake />

      {!homeDetails.isOnLand && (
        <div className="find-retailer-container">
          <div className="constrained-width" id="find-retailer">
            <FindARetailer modelNumber={homeDetails.modelNumber} />
          </div>
        </div>
      )}
      <div className="featured-homes-section">
        {homeDetails && !isPriceLoading && (
          <FeaturedHomes homeDetailsPage={homeDetailsPage} />
        )}
      </div>
      <div className="constrained-width">
        {pageInfo?.disclaimer && (
          <DynamicDisclaimer
            id="disclaimer"
            disclaimerContent={
              homeDetails.isOnLand ? onLandDisclaimer : pageInfo?.disclaimer
            }
            backgroundColor="#FFFFFF"
            textColor="#4E5255"
          />
        )}
      </div>
      <section className="home-details-prefooter">
        {pageInfo?.preFooterActions && (
          <PreFooterSection preFooterActions={pageInfo.preFooterActions} />
        )}
      </section>
    </HomeDetailsStyles>
  );
};

HomeDetails.propTypes = {
  homeDetails: PropTypes.shape({
    images: PropTypes.any,
    modelName: PropTypes.any,
    modelDescription: PropTypes.any,
    modelId: PropTypes.any,
    modelNumber: PropTypes.any,
    tags: PropTypes.any,
    modelFeatures: PropTypes.any,
    virtualTours: PropTypes.any,
    maxSquareFeet: PropTypes.any,
    squareFeet: PropTypes.any,
    baths: PropTypes.any,
    beds: PropTypes.any,
    address1: PropTypes.any,
    address2: PropTypes.any,
    city: PropTypes.any,
    stateProvince: PropTypes.any,
    postalCode: PropTypes.any,
    isOnLand: PropTypes.bool,
    priceLabel: PropTypes.any,
    priceSort: PropTypes.any,
    serialNumber: PropTypes.string,
  }),
  pageInfo: PropTypes.object,
  dealerInfo: PropTypes.object,
};

export default HomeDetails;
